import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import SuperAdminLayout from '../components/SuperAdminLayout';
import subscriptionAddonService from '../services/subscriptionAddonService';
import { toast } from 'react-hot-toast';

const METRIC_OPTIONS = [
  { value: 'USERS', label: 'Users' },
  { value: 'LOCATIONS', label: 'Locations' },
  { value: 'PRODUCTS', label: 'Products' },
  { value: 'FEATURE', label: 'Feature (unlocks plan feature for company)' },
  { value: 'AI_CREDITS', label: 'AI Credits (consumable pack)' },
];

const FEATURE_KEY_OPTIONS = [
  { value: '', label: '— None —' },
  { value: 'whatsapp_bill_share', label: 'WhatsApp sharing' },
  { value: 'loyalty_points', label: 'Loyalty points' },
  { value: 'ecommerce_website', label: 'E-commerce website' },
  { value: 'ai_assistant', label: 'AI Assistant (Ask Gemini)' },
  { value: 'shopify_integration', label: 'Shopify integration' },
  { value: 'webapp', label: 'Web app' },
  { value: 'android_app', label: 'Android app' },
];

const formatCurrency = (amount, currency = 'USD') => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
};

const AddonDefinitionsPage = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    metricType: 'USERS',
    quantityPerUnit: 1,
    priceYearly: '',
    periodMonths: 12,
    currency: 'INR',
    isActive: true,
    sortOrder: 0,
    featureKey: '',
  });

  const loadList = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await subscriptionAddonService.listAddonDefinitions();
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load add-on definitions');
      toast.error(err.message || 'Failed to load add-on definitions');
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
  }, []);

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm({
      name: '',
      metricType: 'USERS',
      quantityPerUnit: 1,
      priceYearly: '',
      periodMonths: 12,
      currency: 'INR',
      isActive: true,
      sortOrder: 0,
      featureKey: '',
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || '',
      metricType: row.metricType || 'USERS',
      quantityPerUnit: row.quantityPerUnit != null ? row.quantityPerUnit : 1,
      priceYearly: row.priceYearly != null ? String(row.priceYearly) : '',
      periodMonths: row.periodMonths != null ? row.periodMonths : 12,
      currency: row.currency || 'INR',
      isActive: row.isActive !== false,
      sortOrder: row.sortOrder != null ? row.sortOrder : 0,
      featureKey: row.featureKey || '',
    });
    setDialogOpen(true);
  };

  const handleFormChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'metricType' && value === 'FEATURE' && !prev.featureKey) {
        const name = (prev.name || '').toLowerCase();
        if (name.includes('ai') && (name.includes('assist') || name.includes('assit') || name.includes('gemini'))) {
          next.featureKey = 'ai_assistant';
        }
      }
      if (field === 'name' && prev.metricType === 'FEATURE' && !prev.featureKey) {
        const name = String(value).toLowerCase();
        if (name.includes('ai') && (name.includes('assist') || name.includes('assit') || name.includes('gemini'))) {
          next.featureKey = 'ai_assistant';
        }
      }
      if (field === 'metricType' && value === 'AI_CREDITS') {
        next.featureKey = '';
      }
      return next;
    });
  };

  const handleSave = async () => {
    const priceYearly = parseFloat(form.priceYearly);
    if (isNaN(priceYearly) || priceYearly < 0) {
      toast.error('Please enter a valid yearly price (non-negative number)');
      return;
    }
    if (!form.name || !form.metricType) {
      toast.error('Name and metric type are required');
      return;
    }
    if (form.metricType === 'FEATURE' && (!form.featureKey || !form.featureKey.trim())) {
      toast.error('Feature add-ons must select a feature key (e.g. AI Assistant → ai_assistant)');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name: form.name.trim(),
        metricType: form.metricType,
        quantityPerUnit: parseInt(form.quantityPerUnit, 10) || 1,
        priceYearly,
        periodMonths: parseInt(form.periodMonths, 10) || 12,
        currency: form.currency || 'INR',
        isActive: form.isActive,
        sortOrder: parseInt(form.sortOrder, 10) || 0,
        featureKey: form.featureKey && form.featureKey.trim() ? form.featureKey.trim() : '',
      };
      if (editingId) {
        await subscriptionAddonService.updateAddonDefinition(editingId, payload);
        toast.success('Add-on definition updated');
      } else {
        await subscriptionAddonService.createAddonDefinition(payload);
        toast.success('Add-on definition created');
      }
      setDialogOpen(false);
      loadList();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeletingId(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingId) return;
    try {
      await subscriptionAddonService.deleteAddonDefinition(deletingId);
      toast.success('Add-on definition deleted');
      setDeleteConfirmOpen(false);
      setDeletingId(null);
      loadList();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to delete');
    }
  };

  return (
    <SuperAdminLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#2c3e50' }}>
            Add-on Definitions
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={{ bgcolor: '#5a9bd4', '&:hover': { bgcolor: '#4a8bc4' } }}
          >
            Add definition
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Define add-ons that clients can purchase (e.g. Extra User, Extra Location, Extra Products). Used when adding add-ons to a company subscription.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper elevation={2} sx={{ borderRadius: 2 }}>
          {loading ? (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Metric</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Feature</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Qty/unit</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Price (year)</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Period (mo)</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Active</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        No add-on definitions. Click &quot;Add definition&quot; to create one.
                      </TableCell>
                    </TableRow>
                  ) : (
                    list.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.metricType}</TableCell>
                        <TableCell>{row.featureKey ? FEATURE_KEY_OPTIONS.find((o) => o.value === row.featureKey)?.label || row.featureKey : '—'}</TableCell>
                        <TableCell>{row.quantityPerUnit}</TableCell>
                        <TableCell>{formatCurrency(row.priceYearly, row.currency)}</TableCell>
                        <TableCell>{row.periodMonths}</TableCell>
                        <TableCell>{row.isActive ? 'Yes' : 'No'}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleOpenEdit(row)} title="Edit">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDeleteClick(row.id)} title="Delete" color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editingId ? 'Edit Add-on Definition' : 'New Add-on Definition'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField fullWidth label="Name" value={form.name} onChange={handleFormChange('name')} placeholder="e.g. Extra 1 User" required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Metric type</InputLabel>
                  <Select value={form.metricType} label="Metric type" onChange={handleFormChange('metricType')}>
                    {METRIC_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth type="number" label="Quantity per unit" value={form.quantityPerUnit} onChange={handleFormChange('quantityPerUnit')} inputProps={{ min: 1 }} helperText="e.g. 1 user, 50 products" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth type="number" label="Price (yearly)" value={form.priceYearly} onChange={handleFormChange('priceYearly')} inputProps={{ min: 0, step: 0.01 }} required helperText="Used for pro-rated when client chooses until plan end" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select value={form.currency} label="Currency" onChange={handleFormChange('currency')}>
                    <MenuItem value="INR">INR</MenuItem>
                    <MenuItem value="USD">USD</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth type="number" label="Period (months)" value={form.periodMonths} onChange={handleFormChange('periodMonths')} inputProps={{ min: 1 }} helperText="For full-period add-on (e.g. 12)" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required={form.metricType === 'FEATURE'}>
                  <InputLabel>Feature key {form.metricType === 'FEATURE' ? '(required)' : '(optional)'}</InputLabel>
                  <Select value={form.featureKey || ''} label={`Feature key ${form.metricType === 'FEATURE' ? '(required)' : '(optional)'}`} onChange={handleFormChange('featureKey')}>
                    {FEATURE_KEY_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value || 'none'} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  When set, adding this add-on to a company unlocks that plan feature for that company only.
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControlLabel control={<Switch checked={form.isActive} onChange={handleFormChange('isActive')} />} label="Active" />
                  <TextField type="number" size="small" label="Sort order" value={form.sortOrder} onChange={handleFormChange('sortOrder')} inputProps={{ min: 0 }} sx={{ width: 100 }} />
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ bgcolor: '#5a9bd4', '&:hover': { bgcolor: '#4a8bc4' } }}>
              {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete confirm */}
        <Dialog open={deleteConfirmOpen} onClose={() => !deletingId && setDeleteConfirmOpen(false)}>
          <DialogTitle>Delete add-on definition?</DialogTitle>
          <DialogContent>This cannot be undone. Companies that already use this add-on will keep their records, but the definition will be removed from the catalog.</DialogContent>
          <DialogActions>
            <Button onClick={() => { setDeleteConfirmOpen(false); setDeletingId(null); }}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleDeleteConfirm}>Delete</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SuperAdminLayout>
  );
};

export default AddonDefinitionsPage;
